import { Space, SpaceVertical, Tooltip } from '@looker/components';
import { BIIcon, BINotification } from '@pulsepointinc/bi-components';
import { DsCheckbox, DsTypography } from '@pulsepointinc/design-system-react';
import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import DotLoader from '../../../../../../components/DotLoader';
import { useFilters } from '../../../../../../hooks/useFilters';
import { usePermissions } from '../../../../../../hooks/usePermissions';
import type { Filter } from '../../../../../../stores/filters';
import useFilterSuggestions from '../../../hooks/useFilterSuggestions';
import { StyledBIToggleButtons } from './styles';

interface EngagementFilterProps {
  onChange: (filter: Filter) => void;
  filterId: string;
  matchTypeFilter?: boolean;
  filterLabel: string;
  reverseOptions?: boolean;
}

const TOOLTIP_MAP: Record<string, string> = {
  'Personal Device':
    'The highest confidence NPI assignment based on device ID and cookie, or 1st party authentication event.',
  'Associated Device':
    'Leverages our cross-device graph that links NPIs assigned by Personal Device Match to additional devices/cookies.',
  'Associated Network':
    'IP based assignment that matches NPIs on signals from Personal Device, Associated Device attribution, and prior digital history (pattern, frequency, etc).',
};

const buildFilter = (filterName: string, expression: string): Filter => ({
  filterName,
  expression,
  enabled: true,
  expressionType: 'target',
});

const EngagementFilter: FC<EngagementFilterProps> = ({
  onChange,
  filterId,
  matchTypeFilter = false,
  filterLabel,
  reverseOptions = false,
}) => {
  const { filters } = useFilters();
  const expression = filters?.[filterId]?.expression ?? '';
  const { hasPermission } = usePermissions();

  const { suggestions, defaultFilterValue, isLoading: suggestionsLoading } = useFilterSuggestions(filterId);

  const toggleOptions = useMemo(() => {
    if (!suggestions?.length) return [];
    return reverseOptions ? [...suggestions].reverse() : suggestions;
  }, [suggestions, reverseOptions]);

  const isLoading = suggestionsLoading;

  const [showWarning, setShowWarning] = useState(false);

  const initialValue = expression || defaultFilterValue || '';
  const [toggle, setToggle] = useState<string>(initialValue);

  useEffect(() => {
    if (!expression && defaultFilterValue) {
      onChange(buildFilter(filterId, defaultFilterValue));
    }
  }, []);

  useEffect(() => {
    if (expression !== toggle) {
      setToggle(expression || '');
    }
  }, [expression]);

  const handleSingleToggleChange = (value: string | null) => {
    const safeValue = value ?? '';
    setToggle(safeValue);
    if (expression && expression !== safeValue) setShowWarning(true);
    onChange(buildFilter(filterId, safeValue));
  };

  const handleCheckboxChange = (option: string, checked: boolean) => {
    const currentValues = toggle.split(',').filter(Boolean);
    const newValues = checked ? [...currentValues, option] : currentValues.filter((v) => v !== option);

    const joined = newValues.join(',');
    setToggle(joined);
    if (expression !== joined) setShowWarning(true);
    onChange(buildFilter(filterId, joined));
  };

  const rightIcons = useMemo(() => {
    if (!matchTypeFilter) return {};

    return toggleOptions.reduce(
      (acc, val) => {
        const tooltip = TOOLTIP_MAP[val];
        if (tooltip) {
          acc[val] = (
            <Tooltip content={tooltip} placement="top">
              <BIIcon icon="Question" />
            </Tooltip>
          );
        }
        return acc;
      },
      {} as Record<string, ReactNode>,
    );
  }, [matchTypeFilter, toggleOptions]);

  const selectedValues = useMemo(() => toggle.split(',').filter(Boolean), [toggle]);

  if (!hasPermission('SmartActionWebHook') && matchTypeFilter) {
    return null;
  }

  const renderFilterControl = () => {
    if (matchTypeFilter) {
      return (
        <SpaceVertical gap="u2">
          {toggleOptions.map((option) => (
            <Space key={option} align="center" gap="u2">
              <DsCheckbox
                label={option}
                checked={selectedValues.includes(option)}
                onCheckedChange={(event: CustomEvent<boolean>) => handleCheckboxChange(option, event.detail)}
              />
              {rightIcons[option]}
            </Space>
          ))}
        </SpaceVertical>
      );
    }

    return (
      <StyledBIToggleButtons
        toggleValues={toggleOptions}
        initialValue={toggle}
        onToggleChange={handleSingleToggleChange}
        rightIcons={rightIcons}
        nullable={false}
      />
    );
  };

  return (
    <SpaceVertical gap="u2">
      <Space gap="u1" align="center">
        <DsTypography variant="label">{filterLabel}</DsTypography>
        {isLoading && <DotLoader />}
      </Space>

      {renderFilterControl()}

      {matchTypeFilter && showWarning && (
        <BINotification text="Match type selection will be carried across all Engagement channels" intent="warning" />
      )}
    </SpaceVertical>
  );
};

export default EngagementFilter;
